// src/context/AuthContext.js - Session-basierte Authentifizierung (Sicher)

import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../utils/apiConfig';
import { SESSION_EXPIRED_EVENT } from '../utils/loginRedirect';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialisierung - prüfe Session-basierte Authentifizierung
  useEffect(() => {
    const initAuth = async () => {
      try {
        console.log('🔍 Prüfe Session-basierte Authentifizierung...');
        
        // Session-Verification mit Timeout
        const verifyUrl = getApiUrl('/api/auth/verify');
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000); // 5s timeout
        
        try {
          const { data } = await axios.post(verifyUrl, {}, { 
            withCredentials: true,
            signal: controller.signal,
            timeout: 5000
          });
          clearTimeout(timeoutId);
          
          if (data && data.success) {
            setUser(data.user || null);
            setIsAuthenticated(Boolean(data.success));
            console.log('✅ Session-Authentifizierung erfolgreich:', data.user && data.user.username);
          } else {
            setIsAuthenticated(false);
            setUser(null);
            console.log('❌ Keine gültige Session gefunden');
          }
        } catch (verifyError) {
          clearTimeout(timeoutId);
          if (verifyError.name === 'AbortError' || verifyError.code === 'ECONNABORTED') {
            console.warn('⏱️ Auth-Verification Timeout - fahre ohne Session fort');
          } else if (verifyError?.response?.status === 401) {
            console.log('ℹ️ Keine aktive Session gefunden (401 bei /api/auth/verify)');
          } else {
            console.warn('⚠️ Auth-Verification fehlgeschlagen:', verifyError.message);
          }
          setIsAuthenticated(false);
          setUser(null);
        }
      } catch (error) {
        console.error('❌ Fehler bei Session-Verification:', error);
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  useEffect(() => {
    const handleSessionExpired = () => {
      setIsAuthenticated(false);
      setUser(null);
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, []);

  const login = async (userData) => {
    try {
      console.log('🔐 Session-basiertes Login wird verarbeitet...');
      
      // Session-basierte Authentifizierung (kein Token-Management mehr)
      if (userData.user) {
        setUser(userData.user);
        setIsAuthenticated(true);
        setLoading(false); // 🔧 WICHTIG: Loading beenden, damit Protected durchlässt
        console.log('✅ Session-Login erfolgreich für:', userData.user.username);
        console.log('🔍 AuthContext State nach Login:', { isAuthenticated: true, loading: false, user: userData.user.username });
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('❌ Login-Fehler:', error);
      return false;
    }
  };

  const logout = async () => {
    try {
      console.log('🚪 Session-Logout wird verarbeitet...');
      
      // Backend-Logout (Session zerstören)
      await axios.post(getApiUrl('/api/auth/logout'), {}, { withCredentials: true });
      
      setIsAuthenticated(false);
      setUser(null);
      console.log('✅ Session-Logout erfolgreich');
    } catch (error) {
      console.error('❌ Logout-Fehler:', error);
      // Auch bei Fehler lokal ausloggen
      setIsAuthenticated(false);
      setUser(null);
    }
  };

  const value = {
    isAuthenticated,
    user,
    loading,
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
